import React, {useEffect, useState} from 'react';
import {Alert, Col, Row, Table, Tabs, Tag} from 'antd';
import ConfirmationAction from '../../CommonComponents/PaymentConfirmationAction';
import SectionLayout from '../../CommonComponents/Layout/sectionLayout';
import SponsorOptionPageHeroText from '../../CommonComponents/SponsorOptionPageHeroText';
import NavigationComponent from '../../CommonComponents/CommonBackButton';
import SponsorPerksCard from '../../CommonComponents/SponsorPerksComponent';
import SponsorDetailCartForm from '../EventSponsorCart/SponsorDetailsCartForm';
import CommonTable from '../../CommonComponents/EventCommonTable';
import {
  attendingDinnerLabel,
  formatCurrencyByLocalChapter,
  formatUtcToLocalDateTime,
  getLocalStorageUserInfo,
  getStatusInfoOfEventRegistration,
  meetingPresentationLabel,
  occupiesRepSlot,
} from '../../../Util/commonUtility';
import useLocalization from '../../../Localization';
import './style.css';

const emptyPendingReps = [];

/**
 * Add vendor reps to an already-paid sponsorship. Shows the sponsorship as bought (package, sponsor
 * details, reps per payment), then lets the sponsor add new reps and pay for them as a new payment.
 */
const AddVendorRepsComponent = ({
  sponsorshipData,
  eventLocalChapterData,
  allEventOptionsFoodData,
  saveRep,
  updateRep,
  removeRep,
  proceedToPayment,
}) => {
  const {ApplicationString} = useLocalization();
  const localUserData = getLocalStorageUserInfo();
  const sponsor = sponsorshipData?.sponsor;
  const sponsorshipOption = sponsorshipData?.sponsorship_option;
  const pendingReps = sponsorshipData?.pending_reps ?? emptyPendingReps;
  // null until this visit's server list is copied in. The table must not mount
  // on [] first — CommonTable would insert a blank draft and wipe the reps
  // that were already added before checkout.
  const serverPendingReps = sponsorshipData ? pendingReps : null;
  const [repsInTable, setRepsInTable] = useState(null);
  const [syncedPendingReps, setSyncedPendingReps] = useState(null);
  const isBlankDraftList = (rows) =>
    !rows?.length ||
    (rows.length === 1 &&
      (rows[0]?.id === '1' || rows[0]?.id === '') &&
      !rows[0]?.attendee_first_name &&
      !rows[0]?.attendee_last_name &&
      !rows[0]?.attendee_email);
  if (serverPendingReps !== syncedPendingReps) {
    setSyncedPendingReps(serverPendingReps);
    setRepsInTable(
      serverPendingReps ? serverPendingReps.map((rep) => ({...rep})) : null,
    );
  } else if (serverPendingReps?.length && isBlankDraftList(repsInTable)) {
    // A blank draft must not replace reps that were already saved. This happens
    // when the table mounts empty for a moment, then the checkout back button
    // or Edit opens the page again.
    setRepsInTable(serverPendingReps.map((rep) => ({...rep})));
  }
  const repsHydrated =
    Boolean(sponsorshipData) && serverPendingReps === syncedPendingReps;
  const perk = sponsorshipOption?.eventSponsorshipPerk;
  const presentationPrice = Number(perk?.attendees_price || 0);
  const dinnerPrice = Number(perk?.dinner_with_speaker_price || 0);
  const formatPrice = (value) =>
    formatCurrencyByLocalChapter(value || 0, eventLocalChapterData, 2, 2);
  const yesNo = (value) =>
    value
      ? ApplicationString['addVendorReps.yes']
      : ApplicationString['addVendorReps.no'];

  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  }, []);

  const repColumns = [
    {
      title: ApplicationString['userEventDetailsModal.name'],
      key: 'name',
      render: (_, record) =>
        `${record?.attendee_first_name || ''} ${
          record?.attendee_last_name || ''
        }`,
    },
    {
      title: ApplicationString['userEventDetailsModal.email'],
      dataIndex: 'attendee_email',
    },
    {
      title: ApplicationString['sponsorshipReps.company'],
      dataIndex: 'attendee_company',
    },
    {
      title: ApplicationString['eventCommonTable.lunch'],
      key: 'food',
      render: (_, record) => {
        const food = record?.event_registration_options?.food;
        if (!food?.name) {
          return ApplicationString['sponsorshipReps.noFood'];
        }
        return food.price || food.price === 0
          ? `${food.name} (${formatPrice(food.price)})`
          : food.name;
      },
    },
    {
      title: meetingPresentationLabel(presentationPrice, ApplicationString),
      dataIndex: 'attending_presentation',
      render: yesNo,
    },
    {
      title: attendingDinnerLabel(dinnerPrice, ApplicationString),
      dataIndex: 'attending_dinner',
      render: yesNo,
    },
  ];

  const paidColumns = [
    ...repColumns,
    {
      title: ApplicationString['userEventDetailsModal.registrationStatus'],
      dataIndex: 'status',
      render: (status) => {
        const {label, color} = getStatusInfoOfEventRegistration(
          status,
          ApplicationString,
        );
        return (
          <Tag
            className={`registration-status-tag registration-status-tag-${color}`}>
            {label}
          </Tag>
        );
      },
    },
  ];

  const transactions = sponsorshipData?.transactions || [];
  const paymentTabs = transactions
    .map((transaction, index) => ({
      key: transaction?.transaction_id || `payment-${index}`,
      label: `${ApplicationString['userEventDetailsModal.payment']}${
        index + 1
      }`,
      children: (
        <>
          <p className='add-vendor-reps-payment-summary'>
            <strong>
              {transaction?.is_original_purchase
                ? ApplicationString['sponsorshipReps.originalPurchase']
                : ApplicationString['sponsorshipReps.addedReps']}
            </strong>
            {transaction?.amount !== null &&
              transaction?.amount !== undefined && (
                <span>
                  {ApplicationString['userEventDetailsModal.totalCost']}:{' '}
                  {formatPrice(transaction.amount)}
                </span>
              )}
            {transaction?.payment_date && (
              <span>
                {ApplicationString['userEventDetailsModal.paymentDate']}:{' '}
                {formatUtcToLocalDateTime(transaction.payment_date) || 'N/A'}
              </span>
            )}
          </p>
          <Table
            rowKey='id'
            size='small'
            pagination={false}
            scroll={{x: 900}}
            columns={paidColumns}
            dataSource={(transaction?.registrations || []).filter(
              (registration) => registration?.is_reps,
            )}
            locale={{emptyText: ApplicationString['addVendorReps.noReps']}}
          />
        </>
      ),
    }))
    .reverse();

  const remainingFreeReps =
    sponsorshipData?.vendor_rep_limit > 0
      ? Math.max(
          sponsorshipData.vendor_rep_limit - sponsorshipData.free_reps_used,
          0,
        )
      : null;
  const pendingTotal = Number(sponsorshipData?.pending_total || 0);

  return (
    <React.Fragment>
      <Row gutter={[0, 10]} className='pbssMainContainerRow'>
        <Col span={24}>
          <SponsorOptionPageHeroText
            title={
              pendingReps.length > 0
                ? ApplicationString['sponsorshipReps.editReps']
                : ApplicationString['addVendorReps.title']
            }
            subTitle={sponsorshipData?.event?.name}
          />
        </Col>
        <Col span={24}>
          <SectionLayout>
            <NavigationComponent
              name={ApplicationString['addVendorReps.backToMyEvents']}
              urlPath='myEvents'
            />
          </SectionLayout>
        </Col>
        {sponsorshipOption && (
          <Col span={24}>
            <SectionLayout>
              <SponsorPerksCard
                hideButton
                usedForEdit
                hideUpload
                multiple={false}
                title={sponsorshipOption?.name}
                sponsorshipType={sponsorshipOption?.sponsorship_type}
                price={sponsorshipOption?.price}
                perks={sponsorshipOption?.eventSponsorshipPerk}
                id={sponsorshipOption?.id}
                eventId={sponsorshipOption?.event_id}
                optionId={sponsorshipOption?.id}
                attendeeDataOptionId={sponsorshipOption?.id}
                eventLocalChapterData={eventLocalChapterData}
              />
            </SectionLayout>
          </Col>
        )}
        {sponsor && (
          <Col span={24}>
            <SectionLayout>
              <SponsorDetailCartForm
                eventSponsorData={sponsor}
                localUserData={localUserData}
                onFinish={() => {}}
                onlyForView
              />
            </SectionLayout>
          </Col>
        )}

        <Col span={24}>
          <SectionLayout>
            <div className='add-vendor-reps-section'>
              <h3 className='add-vendor-reps-heading'>
                {ApplicationString['addVendorReps.existingReps']}
              </h3>
              {sponsorshipData?.vendor_rep_limit > 0 && (
                <p className='add-vendor-reps-meta'>
                  {ApplicationString['sponsorshipReps.freeRepsUsed']}:{' '}
                  {sponsorshipData.free_reps_paid} /{' '}
                  {sponsorshipData.vendor_rep_limit}
                </p>
              )}
              <Tabs prefixCls='user-event-card' items={paymentTabs} />
            </div>
          </SectionLayout>
        </Col>

        <Col span={24}>
          <SectionLayout>
            <div className='add-vendor-reps-section'>
              <h3 className='add-vendor-reps-heading'>
                {ApplicationString['addVendorReps.newReps']}
              </h3>
              {sponsorshipData && !sponsorshipData.can_edit_reps ? (
                <Alert
                  type='info'
                  showIcon
                  message={ApplicationString['sponsorshipReps.changesClosed']}
                />
              ) : (
                sponsorshipData && (
                  <>
                    {!sponsorshipData.can_add_reps ? (
                      <Alert
                        className='add-vendor-reps-limit-alert'
                        type='info'
                        showIcon
                        message={
                          sponsorshipData.has_perk_limits
                            ? ApplicationString[
                                'sponsorshipReps.limitReachedAll'
                              ]
                            : ApplicationString['sponsorshipReps.limitReached']
                        }
                      />
                    ) : (
                      <Alert
                        className='add-vendor-reps-alert'
                        type='info'
                        showIcon
                        message={
                          ApplicationString['sponsorshipReps.paymentNote']
                        }
                        description={
                          !sponsorshipData.can_add_free_reps
                            ? ApplicationString['sponsorshipReps.limitReached']
                            : remainingFreeReps !== null
                            ? `${ApplicationString['sponsorshipReps.remainingFreeReps']}: ${remainingFreeReps}`
                            : null
                        }
                      />
                    )}
                    {repsHydrated &&
                      (sponsorshipData.can_add_reps ||
                        pendingReps.length > 0) && (
                      <CommonTable
                        flush
                        allowAddRow={!!sponsorshipData.can_add_reps}
                        eventId={sponsorshipData?.event?.id}
                        alertInformation={
                          sponsorshipOption?.eventSponsorshipPerk
                            ?.sponsor_perk_content
                        }
                        attendeesDataWithPerks={repsInTable || []}
                        limitAttendees={[
                          ...(sponsorshipData?.transactions || []).flatMap(
                            (transaction) =>
                              (transaction?.registrations || []).filter(
                                (registration) =>
                                  registration?.is_reps &&
                                  occupiesRepSlot(registration?.status),
                              ),
                          ),
                          ...(repsInTable || []),
                        ]}
                        allEventOptionsFoodData={allEventOptionsFoodData}
                        sponsorPerksData={sponsorshipOption}
                        onSave={saveRep}
                        onEdit={updateRep}
                        onDelete={removeRep}
                        eventType={sponsorshipData?.event?.event_type_name}
                        setListAttendeesDataWithPerks={setRepsInTable}
                        setDisablePaymentClicks={() => {}}
                        disablePaymentClicks={false}
                        eventLocalChapterData={eventLocalChapterData}
                        eventISOnline={sponsorshipData?.event?.is_online}
                      />
                    )}
                  </>
                )
              )}

              {pendingReps.length > 0 && (
                <ConfirmationAction
                  heading={ApplicationString['addVendorReps.totalToPay']}
                  submitName={
                    ApplicationString[
                      'userAttendeeCartComponent.confirm&PayText'
                    ]
                  }
                  onConfirm={proceedToPayment}
                  totalCartPrice={{total_price_per_cart: pendingTotal}}
                  usedForSponsor
                  eventLocalChapterData={eventLocalChapterData}
                />
              )}
            </div>
          </SectionLayout>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default AddVendorRepsComponent;
